import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { ProspectMutations } from './mutation-types';
import { ProspectActions } from './action-types';
import {
  ApiOptions,
  CreateProspectPostBody,
  ProspectApiOptions,
  ProspectApiResponse,
  ProspectDetailsResponse,
  ProspectPortfolioDetailsResponse,
} from './types';
import { store } from '../../index';
import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { ClientApiOptions } from '../clientView/types';
import { KpiActionTypes } from '../kpi/action-types';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [ProspectActions.SEARCH_AGGREGATED_PROSPECTS](
    { commit }: AugmentedAction,
    payload: ApiOptions
  ): Promise<void>;
  [ProspectActions.GET_PROSPECT_PORTFOLIOS](
    { commit }: AugmentedAction,
    payload: { clientId: string; loadKPIs: boolean }
  ): Promise<void>;
  [ProspectActions.GET_PROSPECT_DETAILS](
    { commit }: AugmentedAction,
    payload: ClientApiOptions
  ): Promise<void>;
  [ProspectActions.CLEAR_PROSPECT_STATE](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
  [ProspectActions.DELETE_PROSPECT](
    { commit }: AugmentedAction,
    payload: ProspectApiOptions
  ): Promise<void>;
  [ProspectActions.DELETE_PROSPECT_PORTFOLIO](
    { commit }: AugmentedAction,
    payload: ProspectApiOptions
  ): Promise<void>;
  [ProspectActions.CREATE_PROSPECT](
    { commit }: AugmentedAction,
    payload: CreateProspectPostBody
  ): Promise<{
    clientId: string;
  }>;
  [ProspectActions.CREATE_PROSPECT_PORTFOLIO](
    { commit }: AugmentedAction,
    payload: { clientId: string; isExistingClient: boolean }
  ): Promise<{
    scenarioId: string;
  }>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [ProspectActions.SEARCH_AGGREGATED_PROSPECTS](
    { commit },
    filterOptions
  ) {
    try {
      const paramString = combineQueryParams({
        size: filterOptions.pageSize,
        start: filterOptions.pageStart,
        ['sort_descending']: filterOptions.sortDesc,
        ['sort_by']: filterOptions.sortKey,
        ['search_field']: filterOptions.searchField,
        ['search_string']: filterOptions.searchString,
      });

      const filter =
        filterOptions.portfolioFilterField.length > 0
          ? `&filter_field=${filterOptions.portfolioFilterField}&filter_id=${filterOptions.portfolioFilterId}`
          : '';
      const data: ProspectApiResponse = await ServerAPI.get(
        `/prospect${paramString}${filter}`
      );
      commit(ProspectMutations.SET_SEARCH_LIST, data);
    } catch (e) {
      handleError(e);
      commit(ProspectMutations.SET_SEARCH_LIST, {} as ProspectApiResponse);
    }
  },
  async [ProspectActions.GET_PROSPECT_PORTFOLIOS]({ commit }, payload) {
    try {
      commit(ProspectMutations.SET_PROSPECT_PORTFOLIOS_LOADING, true);

      const prospects: Array<ProspectPortfolioDetailsResponse> = await ServerAPI.get(
        `/prospect/${payload.clientId}`
      );
      commit(ProspectMutations.SET_PORTFOLIOS, {
        prospects,
        clientId: payload.clientId,
      });
      if (payload.loadKPIs) {
        await store.dispatch(KpiActionTypes.GET_PROSPECT_KPI_BY_IDS);
      }
    } catch (e) {
      handleError(e);
    } finally {
      commit(ProspectMutations.SET_PROSPECT_PORTFOLIOS_LOADING, false);
    }
  },
  async [ProspectActions.GET_PROSPECT_DETAILS]({ commit }, payload) {
    try {
      const prospectDetails: ProspectDetailsResponse = await ServerAPI.get(
        `/prospect/${payload.clientId}/details`
      );

      commit(ProspectMutations.SET_PROSPECT_DETAILS, prospectDetails);
    } catch (e) {
      handleError(e);
    }
  },
  async [ProspectActions.DELETE_PROSPECT_PORTFOLIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload
  ) {
    try {
      await ServerAPI.delete(
        `/prospect/${payload.clientId}/${payload.scenarioId}`
      );
      await store.dispatch(ProspectActions.GET_PROSPECT_PORTFOLIOS, {
        clientId: payload.clientId,
        loadKPIs: true,
      });
    } catch (e) {
      handleError(e);
    }
  },
  async [ProspectActions.DELETE_PROSPECT](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload
  ) {
    try {
      await ServerAPI.delete(`/prospect/${payload.clientId}`);
    } catch (e) {
      handleError(e);
    }
  },
  async [ProspectActions.CREATE_PROSPECT](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload
  ) {
    try {
      return await ServerAPI.post(`/prospect`, payload);
    } catch (e) {
      handleError(e);
    }
  },
  async [ProspectActions.CREATE_PROSPECT_PORTFOLIO](
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { commit },
    payload
  ) {
    try {
      const url = `/prospect/${payload.clientId}?is_existing_client=${payload.isExistingClient}`;
      return await ServerAPI.post(url);
    } catch (e) {
      handleError(e);
    }
  },
  async [ProspectActions.CLEAR_PROSPECT_STATE]({ commit }) {
    commit(ProspectMutations.CLEAR_STATE, null);
  },
};
